import React, { useEffect, useState } from "react";
import AppModal from "../../components/layout/app/AppModal";
import { useToggleTop } from "../../components/common/ToggleTop";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { changedNothing } from "./remoteUtils";

const FIELDS = [
  { key: "name", label: "Name", max: 40 },
  { key: "brand", label: "Brand Name", max: 40 },
  { key: "model", label: "Model", max: 60 },
];
const EMPTY = { name: "", brand: "", model: "" };

// The app's Create New Remote (app/screens/HashhRemote/NewRemoteModal.js),
// behind "Cannot find your Remote/Brand?": a remote saved by name, brand and
// model with no key codes (source "user"). Its keys are learned with the app's
// Relearn Remote, which reaches the blaster over the home's Wi-Fi.
function NewRemoteDialog({ open, blaster, category, onClose, onAdded }) {
  const toast = useToggleTop();
  const alive = useAlive();
  const [values, setValues] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValues(EMPTY);
  }, [open]);

  const ready = FIELDS.every(({ key }) => values[key].trim());

  const save = async (event) => {
    event.preventDefault();
    if (!ready || saving) return;
    setSaving(true);
    try {
      const result = await UserService.addRemoteToProperty(User.getPropertyId(), {
        name: values.name.trim(),
        deviceId: blaster.deviceId,
        deviceType: category.name_en,
        source: "user",
        brand: values.brand.trim(),
        remote_map: values.model.trim(),
      });
      if (changedNothing(result)) throw new Error("No blaster matched");
    } catch (error) {
      if (!alive.current) return;
      setSaving(false);
      toast("Failed to add remote.", "red");
      return;
    }
    if (!alive.current) return;
    toast("Added remote successfully!", "green");
    onAdded();
  };

  return (
    <AppModal
      open={open}
      onClose={() => !saving && onClose()}
      title="Create New Remote"
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="hx-remote-new-form" className="hx-btn" disabled={!ready || saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form id="hx-remote-new-form" className="hx-name-form" onSubmit={save} noValidate>
        <p className="hx-name-note">
          A {category?.name_en} remote with no keys yet. Learn its keys with Relearn Remote in the HASHh Connect
          app, on the same Wi-Fi as {blaster?.name ?? "the IR blaster"}.
        </p>
        {FIELDS.map(({ key, label, max }) => (
          <React.Fragment key={key}>
            <label className="hx-label" htmlFor={`hx-remote-new-${key}`}>
              {label}
            </label>
            <input
              id={`hx-remote-new-${key}`}
              className="hx-input"
              value={values[key]}
              maxLength={max}
              autoComplete="off"
              disabled={saving}
              onChange={(event) => setValues({ ...values, [key]: event.target.value })}
            />
          </React.Fragment>
        ))}
      </form>
    </AppModal>
  );
}

export default NewRemoteDialog;
